// @flow
import React, { Component } from "react";
import classNames from "classnames";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import DelteteSweepIcon from "@material-ui/icons/DeleteSweep";
import FlagIcon from "@material-ui/icons/Flag";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { withStyles } from "@material-ui/core/styles";

import type { DictionaryType } from "../../../global/types/GeneralTypes";
import type { PersonType } from "../../types/PersonTypes";
import type { ButtonStylesClassesType } from "../../../global/styles/buttonStyles";
import type { IconStylesClassesType } from "../../../global/styles/iconStyles";
import type {
  DrawerStylesClassesType,
  HeaderStylesClassesType
} from "../../../global/styles/generalStyles";
import {
  drawerStyles,
  headerStyles
} from "../../../global/styles/generalStyles";
import { buttonStyles } from "../../../global/styles/buttonStyles";
import { iconStyles } from "../../../global/styles/iconStyles";
import {
  GeneralModalForm,
  SnackbarMessage,
  SingleCheckBox
} from "imagus-components";
import BulkTagForm from "./BulkTagForm";
import BulkDeleteForm from "./BulkDeleteForm";
import SearchBar from "./SearchBar";
import DatabaseList from "./DatabaseList";
import PeopleList from "./PeopleList";
import CreatePersonFormRedux from "../../containers/people/CreatePersonFormRedux";

const PeopleMessages = [
  "Person has been successfully updated",
  "Person has been successfully created",
  "Person has been successfully deleted",
  "These Tags have been successfully added",
  "These People have been successfully deleted"
];

const styles = (theme: DictionaryType): DictionaryType => ({
  ...drawerStyles(theme),
  ...headerStyles(theme),
  ...buttonStyles(theme),
  ...iconStyles(theme),
  leftMargin: {
    marginLeft: "10px"
  },
  iconMargin: {
    marginRight: "5px"
  },
  rightBlock: {
    float: "right"
  }
});

type PeopleDrawerStatesType = {
  open: boolean,
  checkedIds: Array<string>,
  selectedId: string,
  checkAll: number,
  messageId: number,
  createModal: boolean,
  tagModal: boolean,
  deleteModal: boolean,
  snackModal: boolean
};

type PeopleDrawerClassesType = {
  ...DrawerStylesClassesType,
  ...HeaderStylesClassesType,
  ...ButtonStylesClassesType,
  ...IconStylesClassesType,
  leftMargin: DictionaryType,
  iconMargin: DictionaryType,
  rightBlock: DictionaryType
};

type PeopleDrawerPropsType = {
  classes: PeopleDrawerClassesType,
  people: Array<DictionaryType>,
  tags: Array<DictionaryType>,
  updating: DictionaryType,
  showMore: boolean,
  searchQuery: string,
  fetchPeople: Function,
  deletePerson: Function,
  getTags: Function,
  addTags: Function,
  getEnrolments: Function,
  ChipInput: React$ComponentType<*>
};

const initialForm: PeopleDrawerStatesType = {
  open: true,
  checkedIds: [],
  selectedId: "",
  checkAll: 0,
  messageId: 0,
  createModal: false,
  tagModal: false,
  deleteModal: false,
  snackModal: false
};

class PeopleDrawerLayout extends Component<
  PeopleDrawerPropsType,
  PeopleDrawerStatesType
> {
  constructor(props: PeopleDrawerPropsType) {
    super(props);
    this.state = initialForm;
  }

  switchModalStatus = (modalName: string): * => (status: boolean): * => {
    this.setState({ [modalName]: status });
  };

  handleDrawerOpen = () => {
    this.setState({ open: true });
  };

  handleDrawerClose = () => {
    this.setState({ open: false });
  };

  handleToggle = (value: string): * => () => {
    const { checkedIds } = this.state;
    const currentIndex = checkedIds.indexOf(value);
    const newChecked = [...checkedIds];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    this.setState(
      {
        checkedIds: newChecked
      },
      this.changeCheckAllStatus
    );
  };

  changeCheckAllStatus = (): * => {
    if (this.state.checkedIds.length === 0) {
      this.setState({ checkAll: 0 });
    } else {
      if (this.state.checkedIds.length === this.props.people.length) {
        this.setState({ checkAll: 2 });
      } else {
        this.setState({ checkAll: 1 });
      }
    }
  };

  handleCheckBoxChange = (): * => {
    let checkAll = 0;
    if (this.state.checkAll === 0) {
      checkAll = 2;
    } else if (this.state.checkAll === 1) {
      checkAll = 2;
    }

    this.setState(
      {
        checkAll: checkAll
      },
      (): * => {
        if (checkAll === 0) {
          this.setState({ checkedIds: [] });
        } else {
          let checkedIds = [];
          const people = this.props.people;
          people.forEach((person: PersonType): Function => {
            checkedIds.push(person.uid);
          });
          this.setState({ checkedIds: checkedIds });
        }
      }
    );
  };

  selectPerson = (personUid: string): Function => (
    e: SyntheticInputEvent<*>
  ): Function => {
    if (e.target.tagName !== "INPUT") {
      this.setState({ selectedId: personUid });
    }
  };

  fetchPeople = (
    query: string = "",
    isSearch: boolean = false,
    rate: number = 30,
    person_id: string = "",
    doneCall: Function | null = null,
    isAppend: boolean = false
  ) => {
    this.props.fetchPeople(
      query,
      isSearch,
      rate,
      person_id,
      (): * => {
        doneCall && doneCall();
        if (isAppend) {
          this.changeCheckAllStatus();
        }
      },
      isAppend
    );
  };

  clearSelect = (): * => {
    this.setState({ ...initialForm });
  };

  openSnackbar = (val: boolean, messageId: number): * => {
    this.setState(
      {
        messageId: messageId
      },
      (): * => {
        if (val) {
          this.switchModalStatus("snackModal")(true);
        } else {
          this.switchModalStatus("snackModal")(false);
        }
      }
    );
  };

  updateSelect = (personUid: string): * => {
    if (this.state.selectedId === personUid) {
      this.setState({ selectedId: "" });
    }

    let index = this.state.checkedIds.indexOf(personUid);
    let checkedIds = [...this.state.checkedIds];
    if (index > -1) {
      checkedIds.splice(index, 1);
      this.setState({ checkedIds: checkedIds });
    }
  };

  deletePerson = (
    personUid: string,
    doneCall?: Function,
    failCall?: Function
  ): * => {
    this.props.deletePerson(
      personUid,
      (): * => {
        this.updateSelect(personUid);
        doneCall && doneCall();
      },
      failCall && failCall
    );
  };

  render(): * {
    const classes = this.props.classes;
    const { selectedId, open, checkedIds } = this.state;

    const ChipInput = this.props.ChipInput;

    return (
      <div className={classes.root}>
        <div
          className={classNames(
            classes.appBar,
            classes.appHeader,
            open && classes.appBarShift
          )}
        >
          <div
            className={classNames(
              classes.appBarHeader,
              open && classes.appBarHeaderOpen
            )}
          >
            <Button
              type="button"
              variant="contained"
              color="primary"
              className={classNames(classes.darkBtn, classes.leftMargin)}
              onClick={(): Function =>
                this.switchModalStatus("createModal")(true)
              }
            >
              <PersonAddIcon
                className={classNames(classes.lightIcon, classes.iconMargin)}
              />
              Create New Person
            </Button>
            <GeneralModalForm
              title="Create New Person"
              open={this.state.createModal}
              handleModalStatus={this.switchModalStatus("createModal")}
              form={
                <CreatePersonFormRedux
                  onClose={(): * =>
                    this.switchModalStatus("createModal")(false)
                  }
                  setMessage={this.openSnackbar}
                  fromDb={true}
                />
              }
            />
            <div className={classes.rightBlock}>
              <SingleCheckBox
                name="checkAll"
                value={this.state.checkAll === 1 || this.state.checkAll === 2}
                onChange={this.handleCheckBoxChange}
                label={checkedIds.length + " people selected"}
                isIndeter={this.state.checkAll === 1}
              />
              <Button
                type="button"
                variant="contained"
                color="primary"
                className={classNames(classes.darkBtn, classes.leftMargin)}
                onClick={(): Function =>
                  this.switchModalStatus("tagModal")(true)
                }
              >
                <FlagIcon
                  className={classNames(classes.lightIcon, classes.iconMargin)}
                />
                Tag
              </Button>
              <Button
                type="button"
                variant="contained"
                color="primary"
                className={classNames(classes.darkBtn, classes.leftMargin)}
                onClick={(): Function =>
                  this.switchModalStatus("deleteModal")(true)
                }
              >
                <DelteteSweepIcon
                  className={classNames(classes.lightIcon, classes.iconMargin)}
                />
                Delete
              </Button>
              <GeneralModalForm
                title={"Tag " + this.state.checkedIds.length + " People"}
                open={this.state.tagModal}
                handleModalStatus={this.switchModalStatus("tagModal")}
                form={
                  <BulkTagForm
                    onClose={(): * => this.switchModalStatus("tagModal")(false)}
                    people={this.props.people}
                    checkedIds={checkedIds}
                    openSnackbar={this.openSnackbar}
                    tags={this.props.tags}
                    getTags={this.props.getTags}
                    addTags={this.props.addTags}
                    ChipInput={ChipInput}
                  />
                }
              />
              <GeneralModalForm
                title={"Delete " + this.state.checkedIds.length + " People"}
                open={this.state.deleteModal}
                handleModalStatus={this.switchModalStatus("deleteModal")}
                form={
                  <BulkDeleteForm
                    onClose={(): * =>
                      this.switchModalStatus("deleteModal")(false)
                    }
                    people={this.props.people}
                    checkedIds={checkedIds}
                    openSnackbar={this.openSnackbar}
                    deletePerson={this.deletePerson}
                  />
                }
              />
            </div>
          </div>
        </div>
        <Drawer
          variant="permanent"
          classes={{
            paper: classNames(
              classes.drawerPaper,
              !open && classes.drawerPaperClose
            )
          }}
          open={open}
        >
          <div className={classNames(classes.toolbar)}>
            <div className={classNames(classes.toolbarSearch, !open && classes.hide)}>
              <SearchBar
                fetchPeople={this.fetchPeople}
                updating={this.props.updating.searching}
                clearSelect={this.clearSelect}
              />
            </div>
            {open ? (
              <IconButton onClick={this.handleDrawerClose}>
                <ChevronLeftIcon className={classes.shineIcon} />
              </IconButton>
            ) : (
              <IconButton onClick={this.handleDrawerOpen}>
                <MenuIcon className={classes.shineIcon} />
              </IconButton>
            )}
          </div>
          <PeopleList
            open={open}
            selectedId={selectedId}
            checkedIds={checkedIds}
            fetchPeople={this.fetchPeople}
            selectPerson={this.selectPerson}
            handleToggle={this.handleToggle}
            updating={this.props.updating}
            people={this.props.people}
            showMore={this.props.showMore}
            searchQuery={this.props.searchQuery}
          />
        </Drawer>
        <main className={classes.content}>
          <div className={classNames(classes.wrap, !open && classes.wrapClose)}>
            <DatabaseList
              people={this.props.people}
              checkedIds={checkedIds}
              selectedId={selectedId}
              openSnackbar={this.openSnackbar}
              handleToggle={this.handleToggle}
              deletePerson={this.deletePerson}
              updating={this.props.updating}
              getEnrolments={this.props.getEnrolments}
            />
          </div>
        </main>
        <SnackbarMessage
          open={this.state.snackModal}
          handleModalStatus={this.switchModalStatus("snackModal")}
          message={PeopleMessages[this.state.messageId]}
        />
      </div>
    );
  }
}

export default withStyles(styles)(PeopleDrawerLayout);
